import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiCoinStack } from 'react-icons/bi';

import { getRebate } from '../../api/getApi';
import { postWithdrawRebate } from '../../api/postApi';
import LoadingBlock from '../../components/Loading/LoadingBlock';
import useCurrencySymbol from '../../hook/useCurrencySymbol';
import { formatNumber } from '../../utils/formatNumber';
import { waitTime } from '../../utils/waitTime';
import FreezeWrapper from '../StatusModal/FreezeWrapper';
import DetailWrapper from '@/components/DetailWrapper/detailWrapper';
import CloseDetailPage from '@/components/CloseComponent/CloseDetailPage';

const MOCK_DATA = {
  data: {
    bet: 150000
  },
  point: 2450,
  withdraw: 1,
  detail: [
    {
      gameId: 'Evolution Live',
      rebate: 0.5,
      raw_total_bet: 50000,
      rebate_amount: 250
    },
    {
      gameId: 'Pragmatic Slot',
      rebate: 1.2,
      raw_total_bet: 80000,
      rebate_amount: 960
    },
    {
      gameId: 'JILI Fish',
      rebate: 1.0,
      raw_total_bet: 20000,
      rebate_amount: 200
    },
    {
      gameId: 'PG Soft',
      rebate: 1.5,
      raw_total_bet: 40000,
      rebate_amount: 600
    },
    {
      gameId: 'Spadegaming',
      rebate: 1.1,
      raw_total_bet: 40000,
      rebate_amount: 440
    }
  ]
};

const MyRebate = () => {
  const { t } = useTranslation();
  const i18n = (key) => t(`rebate.${key}`);
  const toast = useToast();

  const symbol = useCurrencySymbol();
  const cardBg = useColorModeValue('white', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const skeletonStartColor = useColorModeValue('gray.100', 'gray.700');
  const skeletonEndColor = useColorModeValue('gray.400', 'gray.600');

  const [rebateData, setRebateData] = useState({
    totalBet: 0,
    totalRebate: 0,
    withdrawStatus: 0,
    details: []
  });

  const [loading, setLoading] = useState(true);
  const [isClaiming, setIsClaiming] = useState(false);

  const { totalBet, totalRebate, withdrawStatus, details } = rebateData || {};

  const apiFetching = async () => {
    setLoading(true);
    try {
      const res = await getRebate();
      // 使用 res.point 作為退水數字
      const data = res?.data || res; // 相容不同的結構
      setRebateData({
        totalBet: res?.data?.bet || 0,
        totalRebate: res?.point || 0,
        withdrawStatus: res?.withdraw || 0,
        details: res?.detail || []
      });
    } catch (error) {
      console.error('Error fetching rebate data:', error);
      // 如果 API 失敗，在開發環境模擬數據 (選用)
      // setRebateData({
      //   totalBet: MOCK_DATA.data.bet,
      //   totalRebate: MOCK_DATA.point,
      //   withdrawStatus: MOCK_DATA.withdraw,
      //   details: MOCK_DATA.detail
      // });
    }
    await waitTime(500);
    setLoading(false);
  };

  useEffect(() => {
    apiFetching();
  }, []);

  const handleClaim = async () => {
    if (withdrawStatus !== 1 || totalRebate <= 0) return;

    setIsClaiming(true);
    try {
      await postWithdrawRebate();
      toast({
        title: t('common.success', '成功'),
        description: t('rebate.claim_success', '領取成功'),
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      // Refresh data after claim
      apiFetching();
    } catch (error) {
      console.error('Error claiming rebate:', error);
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <DetailWrapper>
      <CloseDetailPage
        title={t('rebate.my_rebate', '我的退水')}
      ></CloseDetailPage>
      <FreezeWrapper>
        <VStack gap={4} p={4} align="stretch">
          {/* Top Card: Current Rebate Stats */}
          <Box
            bgGradient="linear(to-r, brand.500, brand.600)"
            borderRadius="xl"
            p={6}
            color="white"
            boxShadow="lg"
            position="relative"
            overflow="hidden"
          >
            {/* Decorative background circle */}
            <Box
              position="absolute"
              top="-50%"
              right="-20%"
              boxSize="200px"
              bg="white"
              opacity={0.1}
              borderRadius="full"
            />

            <VStack align="start" spacing={1} mb={6}>
              <HStack justify="space-between" w="100%">
                <VStack align="start" spacing={1}>
                  <HStack>
                    <Icon as={BiCoinStack} />
                    <Text fontSize="sm" fontWeight="medium" opacity={0.9}>
                      {i18n('Total Rewards') || '總退水'}
                    </Text>
                  </HStack>
                  <Skeleton
                    isLoaded={!loading}
                    startColor={skeletonStartColor}
                    endColor={skeletonEndColor}
                    minH="40px"
                    minW="150px"
                  >
                    <Heading size="2xl" fontWeight="black">
                      {symbol} {formatNumber(totalRebate)}
                    </Heading>
                  </Skeleton>
                </VStack>
                <Button
                  colorScheme="whiteAlpha"
                  variant="solid"
                  color="brand.600"
                  bg="white"
                  isLoading={isClaiming}
                  isDisabled={withdrawStatus !== 1 || totalRebate <= 0}
                  onClick={handleClaim}
                  _hover={{ bg: 'gray.100' }}
                  _disabled={{
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    bg: 'whiteAlpha.800'
                  }}
                >
                  {t('rebate.claim', '領取')}
                </Button>
              </HStack>
            </VStack>

            <HStack justify="space-between" align="end" wrap="wrap" spacing={4}>
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" opacity={0.8}>
                  {i18n('Total Bet') || '總投注'}
                </Text>
                <Skeleton
                  isLoaded={!loading}
                  startColor={skeletonStartColor}
                  endColor={skeletonEndColor}
                  minH="24px"
                  minW="100px"
                >
                  <Text fontSize="lg" fontWeight="bold">
                    {symbol} {formatNumber(totalBet)}
                  </Text>
                </Skeleton>
              </VStack>
            </HStack>
          </Box>

          {/* Rebate Details List */}
          <Box>
            <Heading size="md" mb={3} mt={2} color={headingColor}>
              {t('rebate.breakdown', '退水詳細記錄')}
            </Heading>

            <Box bg={cardBg} boxShadow="sm" borderRadius="xl" overflow="hidden">
              <LoadingBlock commonLoading={loading}>
                {(!details || details.length === 0) && !loading ? (
                  <Box p={8} textAlign="center">
                    <Text color="gray.500">
                      {t('rebate.no_rebate_records', '暫無退水記錄')}
                    </Text>
                  </Box>
                ) : (
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th fontSize="xs" pl={4}>
                            {t('rebate.game', '遊戲')}
                          </Th>
                          <Th fontSize="xs" isNumeric>
                            {t('rebate.rebate_percent', '退水%')}
                          </Th>
                          <Th fontSize="xs" isNumeric>
                            {t('rebate.valid_bet', '有效投注')}
                          </Th>
                          <Th fontSize="xs" isNumeric pr={4}>
                            {t('rebate.amount', '金額')}
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {details?.map((detail, idx) => (
                          <Tr key={idx}>
                            <Td pl={4} fontWeight="medium">
                              {detail.gameId}
                            </Td>
                            <Td isNumeric>{detail.rebate}%</Td>
                            <Td isNumeric>
                              {formatNumber(detail.raw_total_bet)}
                            </Td>
                            <Td
                              fontWeight="bold"
                              color="green.500"
                              isNumeric
                              pr={4}
                            >
                              {formatNumber(detail.rebate_amount)}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </LoadingBlock>
            </Box>
          </Box>
        </VStack>
      </FreezeWrapper>
    </DetailWrapper>
  );
};

export default MyRebate;
